/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { CostEstimateSheetInfo } from "./info";
import { CostEstimateSheetService } from "services/cost-estimate-sheet";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const defaultCriteria = [
  "1. Chi phí SXKD dở dang đầu kỳ",
  "2. Chi phí SXKD phát sinh trong kỳ",
  "3. Giá thành sản phẩm, dịch vụ trong kỳ",
  "4. Chi phí SXKD dở dang cuối kỳ"
];

const createRowDefault = (criteria, itemsCount = 1) => ({
  criteria: criteria || "",
  totalAmount: 0,
  itemAmounts: Array(itemsCount).fill(0),
});

const initialValues = {
  unit: "",
  address: "",
  reportMonth: (today.getMonth() + 1).toString(),
  reportYear: today.getFullYear().toString(),
  productName: "",
  itemsHeader: ["Nguồn liệu, vật liệu", "....", "...."], // Mặc định 3 cột
  rows: defaultCriteria.map(c => createRowDefault(c, 3)),
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  // UI helpers for signatures
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function CostEstimateSheetDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await CostEstimateSheetService.getById(id);
          const apiData = response.data ? response.data : response;

          // Parse reportDate -> signingDay/Month/Year
          let sDay = initialValues.signingDay;
          let sMonth = initialValues.signingMonth;
          let sYear = initialValues.signingYear;
          const rDate = apiData.reportDate || "";
          if (rDate) {
            const parts = rDate.split("/");
            if (parts.length === 3) {
              sDay = parts[0];
              sMonth = parts[1];
              sYear = parts[2];
            }
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || apiData.company?.name || "",
            address: apiData.address || apiData.company?.address || "",
            reportMonth: apiData.reportMonth || initialValues.reportMonth,
            reportYear: apiData.reportYear || initialValues.reportYear,
            productName: apiData.productName || "",
            itemsHeader: apiData.itemsHeader || initialValues.itemsHeader,
            rows: apiData.rows || initialValues.rows,
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            reportDate: rDate,
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        }
      } catch (error) {
        console.error("Lỗi load Sổ S37-DN:", error);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation(-1);

  const submitForm = async (fValues) => {
    try {
      const num = (val) => (val && val !== "" && !isNaN(val) ? Number(val) : 0);

      const payload = {
        unit: fValues.unit || "",
        address: fValues.address || "",
        reportMonth: fValues.reportMonth || "",
        reportYear: fValues.reportYear || "",
        productName: fValues.productName || "",
        itemsHeader: fValues.itemsHeader || [],
        rows: (fValues.rows || []).map(row => ({
          criteria: row.criteria || "",
          totalAmount: num(row.totalAmount),
          itemAmounts: (row.itemAmounts || []).map(v => num(v)),
        })),
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
        ledgerClerk: fValues.ledgerClerk || "",
        chiefAccountant: fValues.chiefAccountant || "",
        legalRepresentative: fValues.legalRepresentative || "",
      };

      console.log("🚀 SUBMIT PAYLOAD S37-DN:", payload);

      if (!id) {
        await CostEstimateSheetService.create(payload);
        toast.success("Tạo sổ thành công!");
        navigation("/templates/cost-estimate-sheet");
      } else {
        await CostEstimateSheetService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
    } catch (error) {
      console.error("Lỗi lưu:", error);
      toast.error(error?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(`
      <html>
        <head>
          <title>In Thẻ tính giá thành S37-DN</title>
          <style>@media print { @page { size: A4 landscape; margin: 1cm; } }</style>
        </head>
        <body onload="setTimeout(() => [window.print(), window.close()], 500)">${html}</body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={20}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card sx={{ height: "100%" }}>
                    <SoftBox p={2}>
                      <CostEstimateSheetInfo
                        formData={{ values, setFieldValue, containView }}
                      />
                      <SoftBox mt={10} display="flex" justifyContent="space-between">
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>
                          Trở về
                        </SoftButton>
                        <SoftBox display="flex" gap={2}>
                          {containView ? (
                            <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>
                              In Thẻ Sổ S37-DN
                            </SoftButton>
                          ) : (
                            <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">
                              {id ? "Lưu" : "Tạo mới"}
                            </SoftButton>
                          )}
                        </SoftBox>
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default CostEstimateSheetDetail;
